import { Pipe, PipeTransform } from '@angular/core';
import { DateInterval } from '@homeleite/arch';
import { format, intlFormat, isToday, isTomorrow, isYesterday } from 'date-fns';
type Format = 'date' | 'datetime';

@Pipe({
    name: 'dateInterval',
    pure: true,
    standalone: false
})
export class DateIntervalPipe implements PipeTransform {
  private readonly cache = new WeakMap<DateInterval, string>();

  transform(
    value: DateInterval,
    format: Format,
    customRanges?: Map<DateInterval, string>
  ): string {
    if (!this.cache.has(value)) {
      this.cache.set(value, this.parse(value, format, customRanges));
    }
    return this.cache.get(value);
  }

  private parse(
    value: DateInterval,
    format: Format,
    customRanges?: Map<DateInterval, string>
  ) {
    if (format == null) {
      format = 'date';
    }
    if (value.isWholeDay) {
      return this.calendarOf(value.begin, format);
    }
    if (value.isWholeMonth) {
      return this.monthOf(value.begin);
    }
    if (value.isWholeYear) {
      return this.yearOf(value.begin);
    }
    if (value.isFullMonths) {
      return `${this.monthOf(value.begin)} até ${this.monthOf(value.end)}`;
    }
    if (value.isFullYears) {
      return `${this.yearOf(value.begin)} até ${this.yearOf(value.end)}`;
    }

    return `${this.calendarOf(value.begin, format)} até ${this.calendarOf(
      value.end,
      format
    )}`;
  }

  private calendarOf(date: Date, type: Format) {
    switch (type) {
      case 'datetime':
        return intlFormat(date, {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
        });
      default:
        if (isToday(date)) {
          return 'Hoje';
        } else if (isTomorrow(date)) {
          return 'Amanhã';
        } else if (isYesterday(date)) {
          return 'Ontem';
        } else {
          return intlFormat(date, {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
          });
        }
    }
  }

  private monthOf(date: Date) {
    const str = format(date, 'MMMM');
    return date.getFullYear() === new Date().getFullYear()
      ? str
      : `${str} de ${this.yearOf(date)}`;
  }

  private yearOf(date: Date) {
    return format(date, 'yyyy');
  }
}
